import { Component, OnDestroy, OnInit } from '@angular/core';
import { ContainsSubscriptions } from '../../../core/services/interfaces/contains-subscriptions';
import { Observable, Subject, Subscription } from 'rxjs';
import { unsubscribeFromAll } from '../../../core/pure-functions/rx-helper';
import { OfferDAO } from '../../../core/services/dao/offerDAO';
import { ActivatedRoute, Router } from '@angular/router';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { Offer } from 'anchor-shared/lib/model/entity/offer';
import { CharterDAO } from '../../../core/services/dao/charterDAO';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import moment from 'moment';
import { AppLocation } from '../../../core/models/interface/geo';
import unitedStates from '../../../assets/states/united-states';
import { CustomerDAO } from '../../../core/services/dao/customerDAO';
import { PaymentDAO } from '../../../core/services/dao/paymentDAO';
import { ChickenTestDialogService } from '../../shared/dialogs/chicken-test-dialog/chicken-test-dialog.service';
import { VesselRateCalculationService } from '../../../core/services/vessel-rate-calculation-service';
import { VesselRate } from 'anchor-shared/lib/model/entity/vessel-rate';
import {
  getAddOnsTotal,
  getAddOnsTotalFromPricingMetaData,
  getDurationTypeForCalcService,
  getExtraGuestTotalFromPricingMetaData,
} from 'anchor-shared/lib/service/pure-functions/charter-pricing/charter-pricing-helper';
import { getRateNameForCustomer } from 'anchor-shared/lib/service/pure-functions/get-vessel-rate-name';
import { DialogsService } from '../../../core/services/dialog.service';
import { VesselRateDAO } from '../../../core/services/dao/vesselRateDAO';
import {
  AddOn,
  CustomerAddOn,
  DefinedAddOnType,
  RequiredAddOnUnitType,
} from 'anchor-shared/lib/model/entity/addOn';
import { PaymentType } from 'anchor-shared/lib/model/enum/payment-type';
import { ConstantMan } from '../../../core/services/constantMan';
import { PayoutDAO } from '../../../core/services/dao/payoutDAO';
import { Payout } from 'anchor-shared/lib/model/entity/payout';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { VesselDAO } from '../../../core/services/dao/vesselDAO';
import { RateType } from 'anchor-shared/lib/model/enum/vessel-rate-types';

@Component({
  selector: 'app-charter-details',
  templateUrl: './charter-details.component.html',
  styleUrls: ['./charter-details.component.scss'],
})
export class CharterDetailsComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  // data
  offerId: number;
  offer: Offer;
  charterId: number;
  charter: Charter;
  boat: Vessel;
  boatLocation: AppLocation;
  formattedAddress: string;
  primaryPaymentId: number;
  promoCodeTotal: number = 0;
  discount: number = 0;
  rateTotals;
  extraGuestFee: number = 0;
  customerPrice: number = 0;
  offerAddOns: CustomerAddOn[];
  // legacy pricing
  legacyPricing: boolean;
  vesselRates: VesselRate[];
  charterBaseFee: number = 0;
  // utility
  isMobile: boolean = false;
  loading: boolean = true;
  moment;
  subs: Subscription[] = [];
  optionalAddOnsTotal: number = 0;
  objectEntries = Object.entries;

  constructor(
    private route: ActivatedRoute,
    private offerDAO: OfferDAO,
    private charterDAO: CharterDAO,
    private customerDAO: CustomerDAO,
    private paymentDAO: PaymentDAO,
    private vesselRateDAO: VesselRateDAO,
    private payoutDAO: PayoutDAO,
    private vesselDAO: VesselDAO,
    private calcService: VesselRateCalculationService,
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private dialogService: DialogsService,
    private chickenTestDialog: ChickenTestDialogService,
  ) {
    this.moment = moment;
    const breakpointSub = this.breakpointObserver
      .observe(['(min-width: 960px)'])
      .subscribe((state: BreakpointState) => {
        this.isMobile = !state.matches;
      });
    this.subs.push(breakpointSub);

    this.charterId = this.route.snapshot.params.charterId;
    this.offerId = parseInt(this.route.snapshot.params.offerId);

    const charterSub = this.charterDAO
      .getDetailsById(this.charterId)
      .subscribe((charter) => {
        this.charter = charter;

        if (this.offerId) {
          this.offer = charter.offers.find(
            (offer) => offer.id === this.offerId,
          );

          if (this.offer && this.charter.boat.id !== this.offer.vesselId) {
            this.vesselDAO
              .getByVesselId(this.offer.vesselId)
              .subscribe((vessel) => {
                this.boat = vessel;
              });
          } else {
            this.boat = this.charter.boat;
          }

          if (this.offer.status) {
            this.router.navigate(['/customer/charters']);
          }
        } else if (this.charter.cancelled) {
          this.router.navigate(['/customer/charters']);
        } else {
          this.boat = this.charter.boat;
          const paymentsSub = this.paymentDAO
            .getPaymentsByCharterId(this.charter.id)
            .subscribe((payments) => {
              this.primaryPaymentId = payments.find((payment) => {
                return payment.type === PaymentType.FULL;
              })?.id;
            });
          this.subs.push(paymentsSub);
        }

        if (this.offer) {
          this.extraGuestFee = getAddOnsTotal(
            this.offer.customAddOns || [],
            null,
            true,
          );
        } else {
          this.extraGuestFee = getExtraGuestTotalFromPricingMetaData(
            this.charter.pricingMetaData,
            null,
            true,
          );
        }

        this.optionalAddOnsTotal = getAddOnsTotalFromPricingMetaData(
          charter.pricingMetaData,
          'optional',
          true,
        );
        this.initCharter();
        this.legacyPricing = !!this.charter.pricingMetaData.legacyPricing;
        if (this.legacyPricing) {
          const vesselSub = this.vesselRateDAO
            .getByVesselId(this.charter.boat.id)
            .subscribe((vesselRates) => {
              this.vesselRates = vesselRates;

              this.initRateTotals();

              this.charterDAO
                .getCharterBaseFee(this.charter.id)
                .subscribe(({ baseFee }) => {
                  this.charterBaseFee = baseFee;
                  this.loading = false;
                });
            });
        } else {
          this.initRateTotals();
          this.loading = false;
        }

        this.initCharter();
        this.initCharterTotal();
      });
    this.subs.push(charterSub);
  }

  ngOnInit() {}

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  // data

  initCharter() {
    const { mooringLocation } = this.charter.boat;
    this.boatLocation = {
      position: {
        lat: mooringLocation.parkingLat,
        lng: mooringLocation.parkingLong,
      },
      addressComponents: [],
      formattedAddress: '',
      vicinity: '',
      markerLatitude: 0,
      markerLongitude: 0,
      zoom: 12,
    };

    const state = unitedStates.find((state) => {
      return state.name === mooringLocation.parkingAddressComponents.region;
    });

    const abbreviation = state
      ? state.abbreviation
      : mooringLocation.parkingAddressComponents.region;

    this.formattedAddress = `${mooringLocation.parkingAddressComponents.city}, ${abbreviation}, ${mooringLocation.parkingAddressComponents.country}`;
  }

  initRateTotals() {
    const { originalRates } = this.charter;
    const rates = this.legacyPricing ? this.vesselRates : originalRates;
    const rateTotals = {
      'Boat, Captain, and Crew': 0,
      'Prepaid Fuel': 0,
      Cleaning: 0,
      'Booking Fee': 0,
    };

    (this.offer?.customOriginalRates
      ? this.offer.customOriginalRates
      : rates
    ).forEach((rate) => {
      const name = getRateNameForCustomer(rate);
      const total = this.calculateVesselRate(rate);
      switch (name.toLowerCase()) {
        case 'cleaning':
          rateTotals.Cleaning += total;
          break;
        case 'boat rental':
          rateTotals['Boat, Captain, and Crew'] += total;
          break;
        case 'crew':
          rateTotals['Boat, Captain, and Crew'] += total;
          break;
        case 'captain':
          rateTotals['Boat, Captain, and Crew'] += total;
          break;
        case 'prepaid fuel':
          rateTotals['Prepaid Fuel'] += total;
          break;
        case 'booking fee':
          rateTotals['Booking Fee'] += total;
          break;
      }
    });
    this.rateTotals = rateTotals;

    this.discount = this.calculateDiscount();
    if (this.offer && this.charter.pricingMetaData?.promoCode) {
      const discountableAmount = (
        this.offer.customOriginalRates || this.charter.originalRates
      ).reduce((acc, curr) => {
        if (
          [RateType.Owner, RateType.Captain, RateType.Crew].includes(
            curr.rateType,
          )
        ) {
          acc +=
            Number(
              curr.hourlyRate * (this.offer.duration || this.charter.duration),
            ) *
            (1 + curr.markupPercentage);
        }
        return acc;
      }, 0);

      const { amount, percentageBased, promoDiscountAmount } =
        this.charter.pricingMetaData.promoCode;
      if (percentageBased) {
        this.promoCodeTotal = (amount / 100) * discountableAmount;
      } else {
        this.promoCodeTotal = promoDiscountAmount || 0;
      }
    } else {
      this.promoCodeTotal =
        this.charter.pricingMetaData.promoCode?.promoDiscountAmount || 0;
    }
  }

  initCharterTotal() {
    if (this.offer) {
      const charterWithDuration = { ...this.charter };
      if (this.offer?.duration) {
        charterWithDuration.duration = this.offer.duration;
      }
      this.payoutDAO
        .createBOMockPayoutsForPricingDetails(
          this.offer.customOriginalRates,
          charterWithDuration,
        )
        .subscribe(async (mockPayouts) => {
          const applicablePayouts = mockPayouts.filter(
            (x) => x.name !== 'Affiliate User' && x.type !== 'addOn',
          );
          const price = this.getBuyflowTotal(applicablePayouts);
          this.customerPrice = price - this.promoCodeTotal;
        });
    } else {
      this.customerPrice =
        (this.legacyPricing
          ? this.charterBaseFee
          : this.charter.charterBaseFee) +
        getAddOnsTotalFromPricingMetaData(
          this.charter.pricingMetaData,
          null,
          true,
        ) +
        this.charter.serviceTotal -
        this.promoCodeTotal -
        this.discount;
    }
  }

  initialPayment() {
    const paymentSubject = new Subject<void>();
    this.charterDAO
      .initiateBuyflowPaymentRequest(this.charter.id)
      .subscribe((payments) => {
        this.primaryPaymentId = payments.find((payment) => {
          return payment.type === PaymentType.FULL;
        })?.id;
        paymentSubject.next();
      });

    return paymentSubject.asObservable();
  }

  getBuyflowTotal(payouts): number {
    let total = 0;
    payouts.map((payout) => {
      if (payout.name === 'Booking Fee') {
        total += Number(payout.basePay);
      } else if (payout.type === 'owner') {
        total += parseFloat(payout.basePay) * (1 + payout.markupPercentage);
      } else if (payout.taxPercentage > 0) {
        total += this.calculatePayoutWithMarkup(payout);
      } else {
        total += this.calculatePayoutWithMarkup(payout);
      }
    });

    const requiredAddOns = this.offer.customAddOns;
    total += getAddOnsTotal(requiredAddOns || [], 'required', true);

    return Number(total.toFixed(2));
  }

  calculatePayoutWithMarkup(payout: Payout): number {
    let value = parseFloat(payout.actualPay);
    if (payout.markupPercentage > 0) {
      value += value * payout.markupPercentage;
    }
    return value;
  }

  calculateDiscount() {
    return (
      Number(
        this.charter.discounts?.reduce((acc, curr) => acc + curr.amount, 0),
      ) || 0
    );
  }

  handleConfirmClick() {
    if (this.offer) {
      this.acceptOffer();
    } else {
      if (!this.primaryPaymentId) {
        this.initialPayment().subscribe(() => {
          this.goToCharterPayment();
        });
      } else {
        this.goToCharterPayment();
      }
    }
  }

  goToCharterPayment() {
    const customerSub = this.customerDAO
      .getSessionToken()
      .subscribe((session) => {
        const route = `/charters/${this.charter.id}/payments/${this.primaryPaymentId}/${session.sessionToken}`;
        this.router.navigate([route]);
      });
    this.subs.push(customerSub);
  }

  mergeOptionalAddOns(
    optionalAddOns: AddOn[],
    selectedAddOns: AddOn[],
  ): AddOn[] {
    // set selection and quantities on vessel's optional adds ons from original charter
    const selectedLookup = selectedAddOns.reduce((acc, addOn) => {
      acc[addOn.id] = addOn;
      return acc;
    }, {});

    return optionalAddOns.map((defaultAddOn) => {
      if (selectedLookup[defaultAddOn.id]) {
        return { ...defaultAddOn, ...selectedLookup[defaultAddOn.id] };
      } else {
        return { ...defaultAddOn, quantity: 0 };
      }
    });
  }

  handlePreSubmission() {
    // handle all checks and chicken tests prior to submission
    return new Observable((observer) => {
      const vesselOptionalAddOns =
        this.offer.vessel.addOns?.filter((addOn) => !addOn.required) || [];
      const charterOptionalAddOns = this.charter.pricingMetaData.addOns?.filter(
        (x) => !x.required,
      );

      const optionalAddOns = this.mergeOptionalAddOns(
        vesselOptionalAddOns,
        charterOptionalAddOns,
      );
      const requiredAddOns = this.offer?.customAddOns || [];
      const requiredAddOnsTotal = getAddOnsTotal(
        requiredAddOns,
        'required',
        true,
      );

      if (optionalAddOns.length) {
        const passengersUpdateChickenTest = this.chickenTestDialog
          .open(
            'You have chosing to accept an offer with optional add ons. You will need to select the add ons you want provided with this boat.',
            'Ok, continue',
            'Nevermind',
            60,
            null,
            null,
            null,
            null,
            true,
          )
          .subscribe((submit) => {
            if (submit) {
              const dialogRef = this.dialogService.CustomerAddOnsDialog(
                optionalAddOns,
                this.customerPrice - requiredAddOnsTotal,
                requiredAddOnsTotal,
                null,
                null,
                this.charter.duration,
                this.isMobile,
                false,
              );

              dialogRef.afterClosed().subscribe((selectedAddOns) => {
                if (selectedAddOns) {
                  this.offerAddOns = [...selectedAddOns, ...requiredAddOns];
                  observer.next(true);
                } else {
                  observer.next(false);
                }
                observer.complete();
              });
            } else {
              observer.next(false);
              observer.complete();
            }
          });
        this.subs.push(passengersUpdateChickenTest);
      } else if (requiredAddOns?.length > 0) {
        this.offerAddOns = requiredAddOns;
        observer.next(true);
        observer.complete();
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }

  async acceptOffer() {
    const canSubmit = await this.handlePreSubmission().toPromise();

    if (canSubmit) {
      this.chickenTestDialog
        .open(
          'Accepting this offer will automatically cancel other offers related to this charter – booking details from this offer will be confirmed.',
          'Yes, accept offer',
          "No, don't accept",
          this.isMobile ? 100 : 40,
          null,
          'Accepting this offer',
          null,
          null,
          true,
        )
        .subscribe((accepted) => {
          if (accepted) {
            this.offerDAO
              .acceptOffer(this.offerId, this.offerAddOns)
              .subscribe((charter) => {
                const paymentsSub = this.paymentDAO
                  .getPaymentsByCharterId(this.charter.id)
                  .subscribe((payments) => {
                    this.primaryPaymentId = payments.find((payment) => {
                      return payment.type !== PaymentType.REMAINING;
                    })?.id;

                    const customerSub = this.customerDAO
                      .getSessionToken()
                      .subscribe((session) => {
                        const goToPayment = () => {
                          const route = `/charters/${this.charter.id}/payments/${this.primaryPaymentId}/${session.sessionToken}`;
                          this.router.navigate([route]);
                        };

                        if (!this.primaryPaymentId) {
                          this.initialPayment().subscribe(() => {
                            goToPayment();
                          });
                        } else {
                          goToPayment();
                        }
                      });
                    this.subs.push(customerSub);
                  });
                this.subs.push(paymentsSub);
              });
          }
        });
    }
  }

  declineOffer() {
    this.chickenTestDialog
      .open(
        'Are you sure you want to decline this special offer? Once you decline, this offer will be permanently cancelled.',
        'Decline offer',
        'Go back',
        this.isMobile ? 100 : 40,
        'Declining all offers will result in a cancelled charter.',
        'Decline Offer',
        null,
        null,
        false,
      )
      .subscribe((declined) => {
        if (declined) {
          this.offerDAO.declineOffer(this.offerId).subscribe((charter) => {
            this.router.navigate(['/customer/charters']);
          });
        }
      });
  }

  cancelCharter() {
    this.chickenTestDialog
      .open(
        'Are you sure you want to cancel your charter?',
        'Yes, cancel',
        "No, don't cancel",
        this.isMobile ? 100 : 40,
        'Read our cancellation policy <a href="https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/cancellation-policy-new.pdf" target="_blank">here</a>',
        null,
        null,
        null,
        false,
      )
      .subscribe((canceled) => {
        if (canceled) {
          const updateSub = this.charterDAO
            .customerCancelCharter(this.charter.id)
            .subscribe((res) => {
              if (res) {
                this.router.navigate(['/customer/charters']);
              }
            });
          this.subs.push(updateSub);
        }
      });
  }

  // utility

  createDateString(date, time, additionalTime?) {
    const dateTime = moment(date + ' ' + time);
    if (additionalTime) {
      dateTime.add(additionalTime, 'hour');
    }
    return dateTime.format('ddd, MMMM D, YYYY · h:mm A');
  }

  calculateVesselRate(rate: VesselRate): number {
    return this.calcService.calculateVesselRate(
      rate,
      getDurationTypeForCalcService(
        this.offer?.duration || this.charter.duration,
      ),
      this.offer?.duration || this.charter.duration,
      true,
    );
  }

  getVesselRateTax(): number {
    const rates = this.legacyPricing
      ? this.vesselRates
      : this.offer?.customOriginalRates || this.charter.originalRates;
    return this.calcService.getVesselRateTax(
      rates,
      getDurationTypeForCalcService(
        this.offer?.duration || this.charter.duration,
      ),
      this.offer?.duration || this.charter.duration,
    );
  }

  openBoatRulesModal() {
    const dialogSub = this.dialogService
      .boatRulesDialog(this.charter.boat.instructionsAndRules, this.isMobile)
      .subscribe();
    this.subs.push(dialogSub);
  }

  openDepositDialog() {
    const dialogSub = this.dialogService
      .buyflowDepositDialog(this.charter.boat.depositAmount, this.isMobile)
      .subscribe();
    this.subs.push(dialogSub);
  }

  openCancellationDialog() {
    const dialogSub = this.dialogService
      .buyflowCancellationPolicyDialog(this.isMobile)
      .subscribe();
    this.subs.push(dialogSub);
  }

  navigateBack() {
    this.router.navigate(['customer', 'charters'], {
      queryParams: { activeTab: 'Offers' },
    });
  }

  getDefaultNote() {
    return ConstantMan.GET_DEFAULT_OFFER_NOTE(this.charter.user.firstName);
  }
}
